import { z } from "zod";
import { journeyStateSchema } from "./patient-journey-state";
import { treatmentStateSchema } from "./treatment";

// TES-580 — the admin overview: how the clinic is doing, as numbers.
//
// Counts only, never rows. The admin role has no clinical relationship with
// any patient (TES-491), so this contract must never carry an id, a name or
// anything that resolves to one patient. Every count excludes test patients
// (`Patient.isTest`); erased patients are anonymised, not deleted (GDPR
// Art. 17), so they are reported separately rather than silently counted
// among the live ones.

const count = z.number().int().nonnegative();

export const adminOverviewMetricsSchema = z.object({
  /** When the numbers were computed (ISO). */
  computedAt: z.string(),
  patients: z.object({
    /** Live, non-anonymised patients. */
    total: count,
    newLast7Days: count,
    newLast30Days: count,
    /** GDPR-erased rows: kept for conversion counts, no longer a person. */
    anonymised: count,
  }),
  journey: z.object({
    /** Patients currently in each state — every state present, zeros kept. */
    byState: z.record(journeyStateSchema, count),
    /**
     * Patients that have reached AT LEAST each ladder step (paused/ended are
     * overlays outside the ladder and are excluded). The ladder may be
     * skipped, so this is derived from ordinals, not from the histogram
     * alone — "meeting_booked" here means "meeting_booked or further".
     */
    reachedAtLeast: z.array(
      z.object({ state: journeyStateSchema, patients: count }),
    ),
  }),
  meetings: z.object({
    /** Distinct patients with a first consult booked (not cancelled). */
    patientsBookedFirstConsult: count,
    /** Distinct patients whose consult fee is paid or already consumed. */
    patientsPaidConsultFee: count,
    completed: count,
    noShow: count,
    cancelled: count,
  }),
  subscription: z.object({
    /** Distinct patients a subscription link has been issued for. */
    patientsLinkIssued: count,
    /** Distinct patients who redeemed a link. */
    patientsLinkRedeemed: count,
    /** Stripe status active or trialing. */
    paying: count,
    pastDue: count,
    canceled: count,
  }),
  treatment: z.object({
    byState: z.record(treatmentStateSchema, count),
  }),
  leads: z.object({
    /** Web BMI-quiz leads captured. */
    total: count,
    /** Leads linked to a patient account. */
    converted: count,
  }),
});
export type AdminOverviewMetrics = z.infer<typeof adminOverviewMetricsSchema>;
